import { InsertDriveFileOutlined } from '@mui/icons-material';
import {
    Box,
    BoxProps,
    Button,
    CardHeader,
    Stack,
    SvgIconProps,
    SxProps,
    Typography,
    TypographyProps,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const boxStyles: SxProps = {
    borderColor: 'primary.main',
    borderStyle: 'dashed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    ':hover': {
        backgroundColor: 'surfaceContainerHighest.main',
    },
};

const dropzoneStyles = {
    small: {
        spacing: 1,
        primaryText: {
            variant: 'titleMedium',
            sx: {
                fontSize: '1.25rem',
            },
        } as TypographyProps,
        icon: {
            sx: {
                fontSize: 48,
            },
        } as SvgIconProps,
        box: {
            sx: {
                ...boxStyles,
                width: 320,
                borderRadius: 4,
                height: 148,
                py: 2,
            },
        } as BoxProps,
    },
    large: {
        spacing: 3,
        primaryText: {
            variant: 'titleLarge',
        } as TypographyProps,
        icon: {
            sx: {
                fontSize: 60,
            },
        } as SvgIconProps,
    },
    box: {
        sx: {
            ...boxStyles,
            width: 512,
            borderRadius: 7,
            height: 256,
            display: 'flex',
            alignItems: 'center',
            py: 4,
        },
    } as BoxProps,
};

const DropArea = ({ styles, primaryText, secondaryText }) => {
    return (
        <Stack
            spacing={styles.spacing}
            sx={{
                alignItems: 'center',
                justifyContent: 'center',
                mx: 4,
            }}
        >
            <InsertDriveFileOutlined color="primary" {...styles.icon} />
            <Box textAlign="center">
                <Typography
                    {...styles.primaryText}
                    color="primary.dark"
                    gutterBottom
                >
                    {primaryText}
                </Typography>
                <Typography color="secondary.main">{secondaryText}</Typography>
            </Box>
        </Stack>
    );
};

function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = bytes / Math.pow(1024, i);
    return new Intl.NumberFormat('en', {
        style: 'unit',
        unit: units[i],
        unitDisplay: 'short',
        maximumFractionDigits: 2,
    }).format(size);
}

const AttachedFile = ({ styles, file: File }) => {
    return (
        <Stack spacing={2}>
            <CardHeader
                title={File.name}
                subheader={formatFileSize(File.size)}
                avatar={
                    <InsertDriveFileOutlined color="primary" {...styles.icon} />
                }
            />
            {/* <Stack spacing={2} direction="row" alignSelf="center">
                    <Button
                        variant="tonal"
                        size="medium"
                        color="primary"
                        onClick={() => {
                            // Handle file change logic here
                            console.log('Change file clicked');
                        }}
                    >
                        Change
                    </Button>
                    <Button
                        variant="outlined"
                        size="medium"
                        color="error"
                        onClick={() => {
                            // Handle file remove logic here
                            console.log('Remove file clicked');
                        }}
                    >
                        Remove
                    </Button>
                </Stack> */}
        </Stack>
    );
};

const Dropzone = ({
    size = 'small',
    value: File,
    accept = '.xlsx,.xls,.csv',
    onChange,
    primaryText = 'Drag & Drop Your Files',
    secondaryText = 'or click to browse from your device',
}) => {
    const { t } = useTranslation();
    const styles = dropzoneStyles[size];
    const [file, setFile] = useState<File | null>(File || null);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFIleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        setFile(selectedFile);
        onChange?.(selectedFile);
    };

    return (
        <Box
            {...styles.box}
            bgcolor={isDragging ? 'secondaryContainer.main' : undefined}
            // borderColor={error || true ? 'errorContainer.main' : undefined}
            onClick={() => inputRef.current?.click()}
            onDragEnter={e => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
            }}
            onDragLeave={e => {
                e.preventDefault();
                e.stopPropagation();
                if (
                    e.relatedTarget instanceof Element &&
                    e.currentTarget.contains(e.relatedTarget)
                ) {
                    return;
                }
                setIsDragging(false);
            }}
            onDragOver={(e: DragEvent) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onDrop={(e: DragEvent) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);

                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    const droppedFile = e.dataTransfer.files[0];
                    const fileExtension = droppedFile.name
                        .split('.')
                        .pop()
                        ?.toLowerCase();
                    if (accept.includes(`.${fileExtension}`)) {
                        setFile(droppedFile);
                        onChange?.(droppedFile);
                    } else {
                        setError(
                            `Unsupported file type: .${fileExtension}. Accepted types: ${accept.replace(
                                /,/g,
                                ', ',
                            )}`,
                        );
                        enqueueSnackbar(
                            t('unsupportedFileType', { fileExtension }),
                            {
                                variant: 'error',
                            },
                        );
                        setFile(null);
                        onChange?.(null);
                    }
                }
            }}
        >
            {file ? (
                <AttachedFile styles={styles} file={file} />
            ) : (
                <DropArea
                    styles={styles}
                    primaryText={primaryText}
                    secondaryText={secondaryText}
                />
            )}
            <input
                type="file"
                accept={accept}
                ref={inputRef}
                style={{ display: 'none' }}
                onChange={handleFIleChange}
            />
        </Box>
    );
};

export default Dropzone;
